import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PrimeReactProvider } from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { Avatar } from "primereact/avatar";

import "./i18n/i18n";
import { fetchLatestNews } from "./api/news";
import { fetchRecentObituaries } from "./api/obituaries";
import { useAuth } from "./context/AuthContext";
import { RootState } from "./store/store";
import { setLanguage } from "./store/slices/languageSlice";
import { toggleMode } from "./store/slices/themeSlice";

import ObituariesPage from "./pages/Obituaries";
import NewsPage from "./pages/News";
import ObituaryDetail from "./pages/ObituaryDetail";
import ShopsPage from "./pages/Shops";
import ShopDetail from "./pages/ShopDetail";
import CinemaPage from "./pages/Cinema";
import BakthiPage from "./pages/Bakthi";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import ClassicAppBar from "./components/ClassicAppBar";
import LoginPage from "./pages/Login";
import OAuthCallback from "./pages/OAuthCallback";
import ObituariesAdmin from "./pages/admin/ObituariesAdmin";
import ObituaryForm from "./pages/admin/ObituaryForm";
import ShopsAdmin from "./pages/admin/ShopsAdmin";

// Google AdSense Placeholder Component
// Google AdSense Placeholder Component
const AdSensePlaceholder: React.FC<{
  size?: "banner" | "rectangle" | "square" | "skyscraper";
  className?: string;
}> = ({ size = "banner", className = "" }) => {
  const getAdDimensions = () => {
    switch (size) {
      case "banner":
        return { width: 728, height: 90, label: "Banner Ad" };
      case "rectangle":
        return { width: 300, height: 250, label: "Rectangle Ad" };
      case "square":
        return { width: 250, height: 250, label: "Square Ad" };
      case "skyscraper":
        return { width: 160, height: 600, label: "Skyscraper Ad" };
      default:
        return { width: 728, height: 90, label: "Banner Ad" };
    }
  };

  const { width, height, label } = getAdDimensions();

  return (
    <div
      className={`surface-card border-round shadow-2 flex align-items-center justify-content-center relative overflow-hidden ${className}`}
      style={{
        width: typeof width === "number" ? `${width}px` : width,
        height: `${height}px`,
        backgroundColor: "rgba(0,0,0,0.05)",
        border: "2px dashed rgba(0,0,0,0.2)",
      }}
    >
      <div className="text-center p-2">
        <div className="text-500 text-xs mb-1">Google AdSense</div>
        <div className="font-bold text-600 mb-1">{label}</div>
        <div className="text-500 text-xs">
          {width} × {height}
        </div>
      </div>
    </div>
  );
};

// ClassicAppBar is now imported from components

// Modern Sexy Hero Section
const ModernHeroSection: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div
      className="flex align-items-center relative overflow-hidden"
      style={{
        minHeight: "80vh",
        background: `
          linear-gradient(135deg, 
            rgba(26, 37, 47, 0.95) 0%, 
            rgba(44, 62, 80, 0.9) 25%,
            rgba(211, 84, 0, 0.1) 50%,
            rgba(44, 62, 80, 0.9) 75%,
            rgba(26, 37, 47, 0.95) 100%
          ),
          radial-gradient(circle at 20% 80%, rgba(211, 84, 0, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(52, 152, 219, 0.2) 0%, transparent 50%)
        `,
        borderBottom: "4px solid #d35400",
      }}
    >
      <div
        className="absolute top-0 left-0 w-full h-full"
        style={{
          background: `
            radial-gradient(circle at 30% 30%, rgba(211, 84, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, rgba(52, 152, 219, 0.1) 0%, transparent 50%)
          `,
          animation: "pulse 4s ease-in-out infinite alternate",
        }}
      ></div>

      <div className="container z-2 relative mx-auto px-4">
        <div className="grid align-items-center">
          <div className="col-12 md:col-8 text-center md:text-left">
            <div
              className={`transition-all duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}
            >
              <h1
                className="font-bold text-white mb-3"
                style={{
                  fontSize: "clamp(2.8rem, 5vw, 4.5rem)",
                  fontFamily: "serif",
                  lineHeight: 1.1,
                  textShadow: "3px 3px 6px rgba(0,0,0,0.7)",
                  background:
                    "linear-gradient(45deg, #ffffff 30%, #d35400 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "glow 2s ease-in-out infinite alternate",
                }}
              >
                {t("site.title")}
              </h1>

              <h4
                className="text-white-alpha-90 mb-4 font-normal"
                style={{
                  lineHeight: 1.6,
                  fontSize: "clamp(1.3rem, 2vw, 1.6rem)",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  maxWidth: "600px",
                }}
              >
                {t("site.subtitle")}
              </h4>

              <div className="flex gap-3 flex-wrap justify-content-center md:justify-content-start mb-4">
                <Button
                  label="Latest News"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  className="p-button-lg border-round-3xl font-bold"
                  style={{
                    backgroundColor: "#d35400",
                    borderColor: "#d35400",
                    fontSize: "18px",
                    padding: "1rem 2.5rem",
                    boxShadow: "0 8px 25px rgba(211, 84, 0, 0.4)",
                  }}
                  onClick={() => (window.location.href = "#news")}
                />

                <Button
                  label="Obituaries"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  className="p-button-outlined p-button-lg border-round-3xl font-bold text-white"
                  style={{
                    borderWidth: "3px",
                    // borderColor: 'white', // Handled by class usually, but explicit here if needed or let theme handle
                    fontSize: "18px",
                    padding: "1rem 2.5rem",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  }}
                  onClick={() => (window.location.href = "/obituaries")}
                />
              </div>

              {/* Stats Section */}
              <div className="flex gap-4 flex-wrap justify-content-center md:justify-content-start mt-4">
                {[
                  {
                    icon: "pi pi-align-left",
                    label: "News Articles",
                    value: "500+",
                  },
                  {
                    icon: "pi pi-users",
                    label: "Community Members",
                    value: "2.5K+",
                  },
                  {
                    icon: "pi pi-shopping-bag",
                    label: "Local Businesses",
                    value: "150+",
                  },
                ].map((stat, index) => (
                  <div
                    key={index}
                    className="text-center p-3 border-round bg-white-alpha-10 backdrop-blur-md border-1 border-white-alpha-20"
                  >
                    <div className="text-orange-500 mb-2">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="text-white text-2xl font-bold">
                      {stat.value}
                    </div>
                    <div className="text-white-alpha-80 text-sm">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-12 md:col-4 text-center">
            <div
              className={`transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 transform-scale-100" : "opacity-0 transform-scale-50"}`}
            >
              <div
                className="border-circle flex align-items-center justify-content-center mx-auto relative shadow-8"
                style={{
                  width: "clamp(220px, 20vw, 280px)",
                  height: "clamp(220px, 20vw, 280px)",
                  background: `
                      linear-gradient(135deg, 
                        rgba(211, 84, 0, 0.2) 0%, 
                        rgba(255,255,255,0.1) 50%,
                        rgba(52, 152, 219, 0.2) 100%
                      )
                    `,
                  border: "4px solid #d35400",
                  animation: "float 6s ease-in-out infinite",
                }}
              >
                <h1
                  className="text-white font-normal m-0"
                  style={{
                    fontSize: "clamp(3.5rem, 5vw, 4.5rem)",
                    fontFamily: "serif",
                    textShadow: "3px 3px 6px rgba(0,0,0,0.7)",
                    animation: "pulse 3s ease-in-out infinite",
                  }}
                >
                  யார்
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modern News Section with AdSense
const ModernNewsSection: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = React.useState<
    Array<{ id: number; title: string; summary?: string; imageUrl?: string }>
  >([]);

  React.useEffect(() => {
    fetchLatestNews(6).then(setItems);
  }, []);

  return (
    <div id="news" className="py-8 relative surface-ground">
      <div className="container mx-auto px-4">
        {/* Header with AdSense Banner */}
        <div className="text-center mb-6">
          <div className="transition-all duration-1000">
            <h2
              className="text-900 font-bold mb-3"
              style={{
                fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                fontFamily: "serif",
                background: "linear-gradient(45deg, #1a252f 30%, #d35400 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              <i
                className="pi pi-align-left mr-3 text-5xl"
                style={{ color: "#d35400" }}
              ></i>
              {t("site.latest")}
            </h2>
          </div>

          <p
            className="text-600 mx-auto text-xl line-height-3 mb-4"
            style={{ maxWidth: "800px" }}
          >
            Stay updated with the latest news and events from the Tamil
            community
          </p>

          {/* Top Banner Ad */}
          <div className="flex justify-content-center mb-4">
            <AdSensePlaceholder size="banner" />
          </div>
        </div>

        <div className="grid">
          {items.length === 0 ? (
            // Fallback card when no data from backend
            <div className="col-12 flex justify-content-center">
              <div
                className="p-6 border-round-3xl text-center"
                style={{
                  maxWidth: "600px",
                  background:
                    "linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))",
                  backdropFilter: "blur(10px)",
                  border: "2px dashed rgba(211, 84, 0, 0.3)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                }}
              >
                <i
                  className="pi pi-inbox text-6xl mb-4"
                  style={{ color: "#d35400", opacity: 0.6 }}
                ></i>
                <h3 className="text-2xl font-bold text-900 mb-3">
                  No News Available
                </h3>
                <p className="text-600 line-height-3 mb-4">
                  We couldn't load any news at the moment. The backend service
                  might be unavailable or there are no published articles yet.
                </p>
                <Button
                  label="Refresh Page"
                  icon="pi pi-refresh"
                  className="border-round-3xl"
                  style={{
                    background:
                      "linear-gradient(135deg, #d35400 0%, #e67e22 100%)",
                    border: "none",
                  }}
                  onClick={() => window.location.reload()}
                />
              </div>
            </div>
          ) : (
            items.map((item, index) => (
              <React.Fragment key={item.id}>
                <div className="col-12 md:col-6 lg:col-4 p-3">
                  <div
                    className="h-full border-round-3xl overflow-hidden surface-card relative"
                    style={{
                      background:
                        "linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(211, 84, 0, 0.1)",
                      boxShadow:
                        "0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05) inset",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform =
                        "translateY(-12px) scale(1.02)";
                      e.currentTarget.style.boxShadow =
                        "0 24px 48px rgba(211, 84, 0, 0.25), 0 0 0 2px rgba(211, 84, 0, 0.3) inset";
                      e.currentTarget.style.borderColor =
                        "rgba(211, 84, 0, 0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform =
                        "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05) inset";
                      e.currentTarget.style.borderColor =
                        "rgba(211, 84, 0, 0.1)";
                    }}
                  >
                    {/* Image with gradient overlay */}
                    <div
                      className="relative overflow-hidden"
                      style={{ height: "240px" }}
                    >
                      <img
                        src={
                          item.imageUrl ||
                          `https://images.unsplash.com/photo-${[
                            "1504711434969-e33886168f5c",
                            "1585829365295-ab7cd400c167",
                            "1495020689067-958852a7765e",
                            "1633356122544-f134324a6cee",
                            "1586339392399-df486e01b09f",
                            "1436491865332-7a61a109cc05",
                          ][item.id % 6]
                          }?w=800&h=600&fit=crop`
                        }
                        alt={item.title}
                        className="w-full h-full object-cover"
                        style={{
                          transition:
                            "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                      {/* Gradient Overlay */}
                      <div
                        className="absolute bottom-0 left-0 w-full h-full"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(26, 37, 47, 0.95) 0%, rgba(26, 37, 47, 0.3) 50%, transparent 100%)",
                          pointerEvents: "none",
                        }}
                      />
                      {/* Latest Badge */}
                      <div className="absolute top-3 right-3">
                        <Chip
                          label="Latest"
                          className="font-bold shadow-4"
                          style={{
                            background:
                              "linear-gradient(135deg, #d35400 0%, #e67e22 100%)",
                            color: "white",
                            border: "2px solid rgba(255, 255, 255, 0.3)",
                            animation: "pulse 2s ease-in-out infinite",
                          }}
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h5
                        className="text-xl font-bold text-900 mb-3 line-height-3"
                        style={{
                          minHeight: "65px",
                          fontFamily: "serif",
                          background:
                            "linear-gradient(135deg, #1a252f 0%, #d35400 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {item.title}
                      </h5>

                      <p
                        className="text-600 line-height-3 mb-4 text-sm"
                        style={{ minHeight: "60px" }}
                      >
                        {item.summary ||
                          "Read more about this important news story..."}
                      </p>

                      {/* Meta Info */}
                      <div
                        className="flex align-items-center justify-content-between mb-4 pb-3"
                        style={{
                          borderBottom: "1px solid rgba(211, 84, 0, 0.1)",
                        }}
                      >
                        <div className="flex align-items-center gap-2">
                          <i className="pi pi-clock text-orange-500"></i>
                          <span className="text-xs text-500 font-semibold">
                            2 hours ago
                          </span>
                        </div>
                        <div className="flex align-items-center gap-2">
                          <i className="pi pi-eye text-orange-500"></i>
                          <span className="text-xs text-500 font-semibold">
                            1.2K views
                          </span>
                        </div>
                      </div>

                      <Button
                        label="Read More"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        className="w-full border-round-3xl font-bold"
                        style={{
                          background:
                            "linear-gradient(135deg, #d35400 0%, #e67e22 100%)",
                          border: "none",
                          padding: "0.75rem 1.5rem",
                          boxShadow: "0 4px 15px rgba(211, 84, 0, 0.3)",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(211, 84, 0, 0.5)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(211, 84, 0, 0.3)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                        onClick={() => { }}
                      />
                    </div>
                  </div>
                </div>

                {/* AdSense Rectangle after every 3rd item */}
                {(index + 1) % 3 === 0 && (
                  <div className="col-12 flex justify-content-center my-2">
                    <AdSensePlaceholder size="banner" />
                  </div>
                )}
              </React.Fragment>
            ))
          )}
        </div>

        {/* Bottom Banner Ad */}
        <div className="flex justify-content-center mt-6">
          <AdSensePlaceholder size="banner" />
        </div>
      </div>
    </div>
  );
};

const ModernObituariesSection: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = React.useState<
    Array<{
      id: number;
      name: string;
      age?: number;
      residence?: string;
      photoUrl?: string;
    }>
  >([]);

  React.useEffect(() => {
    fetchRecentObituaries(6).then(setItems);
  }, []);

  return (
    <div id="obituaries" className="py-8 relative bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6">
          <div className="transition-all duration-1000">
            <h2
              className="text-900 font-bold mb-3"
              style={{
                fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                fontFamily: "serif",
                background: "linear-gradient(45deg, #1a252f 30%, #d35400 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              <i
                className="pi pi-users mr-3 text5xl"
                style={{ color: "#d35400" }}
              ></i>
              {t("site.obituaries")}
            </h2>
          </div>

          <p
            className="text-600 mx-auto text-xl line-height-3 mb-4"
            style={{ maxWidth: "800px" }}
          >
            Remembering our loved ones and honoring their memory
          </p>
        </div>

        <div className="grid">
          {items.length === 0 ? (
            // Fallback card when no data from backend
            <div className="col-12 flex justify-content-center">
              <div
                className="p-6 border-round-3xl text-center"
                style={{
                  maxWidth: "600px",
                  background:
                    "linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))",
                  backdropFilter: "blur(10px)",
                  border: "2px dashed rgba(211, 84, 0, 0.3)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                }}
              >
                <i
                  className="pi pi-heart text-6xl mb-4"
                  style={{ color: "#d35400", opacity: 0.6 }}
                ></i>
                <h3 className="text-2xl font-bold text-900 mb-3">
                  No Obituaries Available
                </h3>
                <p className="text-600 line-height-3 mb-4">
                  We couldn't load any obituaries at the moment. The backend
                  service might be unavailable or there are no published
                  obituaries yet.
                </p>
                <Button
                  label="Refresh Page"
                  icon="pi pi-refresh"
                  className="border-round-3xl"
                  style={{
                    background:
                      "linear-gradient(135deg, #d35400 0%, #e67e22 100%)",
                    border: "none",
                  }}
                  onClick={() => window.location.reload()}
                />
              </div>
            </div>
          ) : (
            items.map((item, index) => (
              <React.Fragment key={item.id}>
                <div className="col-12 md:col-6 lg:col-4 p-3">
                  <div
                    className="h-full border-round-3xl overflow-hidden surface-card relative"
                    style={{
                      background:
                        "linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(211, 84, 0, 0.1)",
                      boxShadow:
                        "0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05) inset",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform =
                        "translateY(-12px) scale(1.02)";
                      e.currentTarget.style.boxShadow =
                        "0 24px 48px rgba(211, 84, 0, 0.25), 0 0 0 2px rgba(211, 84, 0, 0.3) inset";
                      e.currentTarget.style.borderColor =
                        "rgba(211, 84, 0, 0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform =
                        "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05) inset";
                      e.currentTarget.style.borderColor =
                        "rgba(211, 84, 0, 0.1)";
                    }}
                  >
                    {/* Photo with gradient overlay */}
                    <div
                      className="relative overflow-hidden"
                      style={{ height: "280px" }}
                    >
                      <img
                        src={
                          item.photoUrl ||
                          `https://images.unsplash.com/photo-${[
                            "1438761681033-6461ffad8d80",
                            "1507003211169-0a1dd7228f2d",
                            "1544005313-94ddf0286df2",
                            "1500648767791-00dcc994a43e",
                            "1573497019940-1c28c88b4f3e",
                            "1552374196-1ab2a1c593e8",
                          ][item.id % 6]
                          }?w=600&h=800&fit=crop&crop=faces`
                        }
                        alt={item.name}
                        className="w-full h-full object-cover"
                        style={{
                          filter: "grayscale(20%)",
                          transition:
                            "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                          e.currentTarget.style.filter = "grayscale(0%)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.filter = "grayscale(20%)";
                        }}
                      />
                      {/* Gradient Overlay */}
                      <div
                        className="absolute bottom-0 left-0 w-full h-full"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(26, 37, 47, 0.9) 0%, rgba(211, 84, 0, 0.2) 50%, transparent 100%)",
                          pointerEvents: "none",
                        }}
                      />
                      {/* In Memory Badge */}
                      <div className="absolute top-3 right-3">
                        <Chip
                          label="In Memory"
                          className="font-bold shadow-4"
                          style={{
                            background:
                              "linear-gradient(135deg, #1a252f 0%, #d35400 100%)",
                            color: "white",
                            border: "2px solid rgba(255, 255, 255, 0.3)",
                          }}
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 text-center">
                      <h5
                        className="text-2xl font-bold text-900 mb-3 font-serif"
                        style={{
                          background:
                            "linear-gradient(135deg, #1a252f 0%, #d35400 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {item.name}
                      </h5>

                      {item.age && (
                        <Chip
                          label={`Age ${item.age}`}
                          className="mb-3 font-bold"
                          style={{
                            background: "rgba(211, 84, 0, 0.1)",
                            color: "#d35400",
                            border: "1px solid rgba(211, 84, 0, 0.3)",
                          }}
                        />
                      )}

                      <p
                        className="text-600 line-height-3 mb-4 text-sm"
                        style={{ minHeight: "50px" }}
                      >
                        {item.residence ||
                          "Loved and remembered by family and friends"}
                      </p>

                      {/* Location Info */}
                      <div
                        className="flex align-items-center justify-content-center gap-2 mb-4 pb-3"
                        style={{
                          borderBottom: "1px solid rgba(211, 84, 0, 0.1)",
                        }}
                      >
                        <i className="pi pi-map-marker text-orange-500"></i>
                        <span className="text-xs text-500 font-semibold">
                          {item.residence || "Location"}
                        </span>
                      </div>

                      <Button
                        label="View Details"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        className="w-full border-round-3xl font-bold"
                        style={{
                          background: "transparent",
                          border: "2px solid #d35400",
                          color: "#d35400",
                          padding: "0.75rem 1.5rem",
                          boxShadow: "0 4px 15px rgba(211, 84, 0, 0.2)",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "linear-gradient(135deg, #d35400 0%, #e67e22 100%)";
                          e.currentTarget.style.color = "white";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(211, 84, 0, 0.4)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "#d35400";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(211, 84, 0, 0.2)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* AdSense Rectangle after every 3rd item */}
                {(index + 1) % 3 === 0 && (
                  <div className="col-12 flex justify-content-center my-2">
                    <AdSensePlaceholder size="banner" />
                  </div>
                )}
              </React.Fragment>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ModernShopsSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div id="shops" className="py-8 relative surface-ground">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6">
          <div className="transition-all duration-1000">
            <h2
              className="text-900 font-bold mb-3"
              style={{
                fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                fontFamily: "serif",
                background: "linear-gradient(45deg, #1a252f 30%, #d35400 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              <i
                className="pi pi-shopping-bag mr-3 text-5xl"
                style={{ color: "#d35400" }}
              ></i>
              {t("site.shops")}
            </h2>
          </div>

          <p
            className="text-600 mx-auto text-xl line-height-3 mb-4"
            style={{ maxWidth: "800px" }}
          >
            Discover Tamil businesses and services in your community
          </p>
        </div>

        <div className="grid">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <React.Fragment key={item}>
              <div className="col-12 md:col-6 lg:col-4 p-3">
                <Card className="h-full border-round-2xl overflow-hidden shadow-4 transition-all transition-duration-400 hover:shadow-8 hover:-translate-y-2 surface-card">
                  <div className="p-4 text-center relative">
                    <div className="absolute top-0 right-0 mt-3 mr-3">
                      <Chip
                        label="Verified"
                        className="bg-orange-500 text-white font-bold"
                      />
                    </div>

                    <Avatar
                      icon="pi pi-shopping-bag"
                      className="mx-auto mb-3 border-circle surface-900"
                      style={{
                        width: "120px",
                        height: "120px",
                        backgroundColor: "#d35400",
                        border: "4px solid white",
                        boxShadow: "0 8px 25px rgba(211, 84, 0, 0.3)",
                      }}
                    ></Avatar>

                    <h5 className="text-2xl font-bold text-900 mb-2 font-serif">
                      Tamil Shop {item}
                    </h5>

                    <p
                      className="text-700 line-height-3 mb-3"
                      style={{ minHeight: "50px" }}
                    >
                      Traditional Tamil products and services for the community
                    </p>

                    <div className="flex justify-content-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className="pi pi-star-fill text-yellow-500 text-xl"
                        ></i>
                      ))}
                      <span className="text-600 ml-1">(4.8)</span>
                    </div>

                    <div className="flex align-items-center justify-content-center gap-2 mb-3">
                      <div className="flex align-items-center gap-1">
                        <i className="pi pi-map-marker text-orange-500"></i>
                        <span className="text-sm text-600">Local Area</span>
                      </div>
                      <div className="flex align-items-center gap-1">
                        <i className="pi pi-chart-line text-orange-500"></i>
                        <span className="text-sm text-600">Popular</span>
                      </div>
                    </div>

                    <Button
                      label="View Shop"
                      className="w-full font-bold"
                      style={{
                        backgroundColor: "#d35400",
                        borderColor: "#d35400",
                      }}
                    />
                  </div>
                </Card>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { ready, authenticated } = useAuth();

  if (!ready) {
    return null;
  }

  if (!authenticated) {
    const redirect = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    );
  }

  return <>{children}</>;
};

function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const themeMode = useSelector(
    (state: RootState) => state.theme?.mode || "light",
  );

  const handleLanguageChange = (lang: "en" | "ta" | "de") => {
    i18n.changeLanguage(lang);
    dispatch(setLanguage(lang));
  };

  const handleToggleMode = () => {
    dispatch(toggleMode());
  };

  const appBody = (
    <PrimeReactProvider>
      <div className="min-h-screen flex flex-column">
        <ClassicAppBar
          mode={themeMode}
          onToggleMode={handleToggleMode}
          onChangeLang={handleLanguageChange}
        />

        <ModernHeroSection />

        <ModernNewsSection />
        <ModernObituariesSection />
        <ModernShopsSection />

        <footer className="bg-gray-900 py-6 mt-auto">
          <div className="container mx-auto px-4">
            <div className="grid">
              <div className="col-12 md:col-4">
                <h6 className="text-white font-bold mb-3 font-serif text-xl">
                  YarlHub
                </h6>
                <p className="text-white-alpha-80 line-height-3">
                  Connecting the Tamil community through news, obituaries, and
                  local businesses.
                </p>
              </div>
              <div className="col-12 md:col-4">
                <h6 className="text-white font-bold mb-3 font-serif text-xl">
                  Quick Links
                </h6>
                <div className="flex flex-column gap-1">
                  <Button
                    label="Latest News"
                    className="p-button-text text-white-alpha-80 hover:text-orange-500 justify-content-start p-0"
                    onClick={() => (window.location.href = "/news")}
                  />
                  <Button
                    label="Obituaries"
                    className="p-button-text text-white-alpha-80 hover:text-orange-500 justify-content-start p-0"
                    onClick={() => (window.location.href = "/obituaries")}
                  />
                  <Button
                    label="Tamil Shops"
                    className="p-button-text text-white-alpha-80 hover:text-orange-500 justify-content-start p-0"
                    onClick={() => (window.location.href = "/shops")}
                  />
                  <Button
                    label="Tamil Cinema"
                    className="p-button-text text-white-alpha-80 hover:text-orange-500 justify-content-start p-0"
                    onClick={() => (window.location.href = "/cinema")}
                  />
                  <Button
                    label="Bakthi & Horoscope"
                    className="p-button-text text-white-alpha-80 hover:text-orange-500 justify-content-start p-0"
                    onClick={() => (window.location.href = "/bakthi")}
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <h6 className="text-white font-bold mb-3 font-serif text-xl">
                  Contact
                </h6>
                <p className="text-white-alpha-80 line-height-3">
                  Email: info@yarlhub.com
                  <br />
                  Phone: +1 (555) 123-4567
                </p>
              </div>
            </div>
            <div className="border-top-1 border-white-alpha-20 mt-4 pt-4 text-center">
              <p className="text-white-alpha-60 text-sm">
                &copy; 2024 YarlHub. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </PrimeReactProvider>
  );
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <Routes>
          <Route path="/" element={appBody} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth2/callback" element={<OAuthCallback />} />
          <Route
            path="/admin"
            element={<Navigate to="/admin/obituaries" replace />}
          />
          <Route
            path="/admin/obituaries"
            element={
              <RequireAuth>
                <ObituariesAdmin />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/obituaries/new"
            element={
              <RequireAuth>
                <ObituaryForm />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/obituaries/:id/edit"
            element={
              <RequireAuth>
                <ObituaryForm />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shops"
            element={
              <RequireAuth>
                <ShopsAdmin />
              </RequireAuth>
            }
          />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/obituaries" element={<ObituariesPage />} />
          <Route path="/obituaries/:id" element={<ObituaryDetail />} />
          <Route path="/shops" element={<ShopsPage />} />
          <Route path="/shops/:id" element={<ShopDetail />} />
          <Route path="/cinema" element={<CinemaPage />} />
          <Route path="/bakthi" element={<BakthiPage />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
